import React, { useEffect, useState } from 'react';
import {
  Table, Button, Tag, Typography, Space, Tooltip, Popconfirm, message, Empty, Card,
} from 'antd';
import {
  PlusOutlined, ThunderboltOutlined, PlayCircleOutlined, PauseCircleOutlined,
  EditOutlined, DeleteOutlined, BranchesOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title, Text } = Typography;

const TRIGGER_LABEL = {
  list_join:           'When subscriber joins a list',
  tag_added:           'When a tag is added',
  manual:              'Manual enrollment',
  automation_finished: 'After finishing another automation',
  campaign_finished:   'After a campaign completes',
};

const STATUS_TAG = {
  draft:  { color: 'default', label: 'Draft' },
  active: { color: 'success', label: 'Active' },
  paused: { color: 'warning', label: 'Paused' },
};

export default function Automations() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/automations');
      setRows(res.data || []);
    } catch (e) {
      if (e.response?.status === 403 && e.response?.data?.upgrade) {
        // Pro-gated — handled by interceptor modal; keep list empty
      } else {
        message.error(e.response?.data?.message || 'Failed to load automations');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (row) => {
    setBusyId(row.id);
    try {
      const action = row.status === 'active' ? 'pause' : 'activate';
      await axios.post(`/api/automations/${row.id}/${action}`);
      message.success(action === 'activate' ? 'Automation activated' : 'Automation paused');
      await load();
    } catch (e) {
      message.error(e.response?.data?.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row) => {
    try {
      await axios.delete(`/api/automations/${row.id}`);
      message.success('Automation deleted');
      await load();
    } catch (e) {
      message.error(e.response?.data?.message || 'Delete failed');
    }
  };

  const columns = [
    {
      title: 'Automation', key: 'name', width: 280, ellipsis: true,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ cursor: 'pointer' }} onClick={() => navigate(`/automations/${r.id}/edit`)}>{r.name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{TRIGGER_LABEL[r.trigger_type] || r.trigger_type}</Text>
        </Space>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (s) => { const t = STATUS_TAG[s] || STATUS_TAG.draft; return <Tag color={t.color}>{t.label}</Tag>; },
    },
    { title: 'Steps', dataIndex: 'step_count', key: 'steps', width: 80, render: (n) => n || 0 },
    {
      title: 'Enrolled', key: 'enrolled', width: 150,
      render: (_, r) => (
        <Text style={{ fontSize: 13 }}>
          {r.enrolled || 0} total · <Text type="success">{r.active || 0} active</Text>
        </Text>
      ),
    },
    {
      title: '', key: 'actions', width: 150, align: 'right',
      render: (_, r) => (
        <Space size={2}>
          <Tooltip title={r.status === 'active' ? 'Pause' : 'Activate'}>
            <Button
              type="text" size="small"
              loading={busyId === r.id}
              icon={r.status === 'active' ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={() => toggle(r)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => navigate(`/automations/${r.id}/edit`)} />
          </Tooltip>
          <Popconfirm title="Delete this automation?" description="Enrollments will be removed." okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => remove(r)}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}><BranchesOutlined /> Automations</Title>
          <Text type="secondary">Trigger-based email sequences — welcome series, onboarding, re-engagement.</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/automations/create')}>
          New Automation
        </Button>
      </div>

      <Card bodyStyle={{ padding: 0 }} style={{ borderRadius: 14 }}>
        <Table
          rowKey="id"
          dataSource={rows}
          columns={columns}
          loading={loading}
          pagination={{ pageSize: 15, hideOnSinglePage: true }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: (
            <Empty
              image={<ThunderboltOutlined style={{ fontSize: 40, color: 'var(--text-3)' }} />}
              description="No automations yet. Create your first welcome series."
            />
          ) }}
        />
      </Card>
    </div>
  );
}
